import type { AnswerValue, IterationContext } from '../dto/survey/survey-answers.types';
import type {
  EndNode,
  EnterWhen,
  MultiChoiceNode,
  Next,
  NodeId,
  RepeatBlockNode,
  SurveyNode,
  SurveySchema,
} from '../dto/survey/survey-schema.types';

export type NextResolution =
  | { kind: 'goto'; nodeId: NodeId; iterationContext: IterationContext | null }
  | { kind: 'end-of-survey' };

/**
 * True when the node terminates the survey WITHOUT producing a document
 * (`type: 'end'` + `finish: 'exit'`). Nodes without `finish` (or with
 * `finish: 'submit'`) keep the legacy behaviour: submit + document.
 * Single shared derivation used by the frontend runner, the admin preview
 * and the backend submit guard so the three can never drift apart.
 */
export function isExitEnd(node: SurveyNode): node is EndNode {
  return node.type === 'end' && node.finish === 'exit';
}

export type Answers = { byNode: Record<string, AnswerValue> } | null;

export function iterationCount(rb: RepeatBlockNode, answers: Answers): number {
  if (rb.iterationSource.kind === 'fixed-count') {
    return rb.iterationSource.count;
  }
  if (!answers) {
    return 1;
  }
  const a = answers.byNode[rb.iterationSource.answerNodeId];
  if (!a || a.kind !== 'number') {
    return 1;
  }
  return Math.max(0, Math.floor(a.value));
}

/**
 * Multi-choice jump target: the FIRST chosen option id that has a declared
 * jump, scanning `node.options` in CATALOG order — never the order the
 * operator tapped them in. Deterministic and independent of tap order,
 * mirroring `sortByCatalogOrder`/`catalogOrderedMultiSelect` used elsewhere
 * for the same reason (NC domain profile, PDF rendering).
 */
function firstCatalogOrderedJumpTarget(
  node: MultiChoiceNode,
  next: Extract<Next, { kind: 'jump-by-option' }>,
  chosenOptionIds: readonly string[],
): NodeId | 'END' | undefined {
  const chosen = new Set(chosenOptionIds);
  for (const option of node.options) {
    if (chosen.has(option.id) && next.perOption[option.id] !== undefined) {
      return next.perOption[option.id];
    }
  }
  return undefined;
}

function staticTarget(node: SurveyNode, answer: AnswerValue): string {
  if (node.type === 'end') {
    return 'END';
  }
  const n = (node as Exclude<SurveyNode, { type: 'end' }>).next;
  if (!n) {
    return 'END';
  }
  if (n.kind === 'linear') {
    return n.nextNodeId;
  }
  if (n.kind === 'jump-by-option' && answer.kind === 'single') {
    return n.perOption[answer.optionId] ?? n.default;
  }
  if (n.kind === 'jump-by-option' && answer.kind === 'multi' && node.type === 'multi-choice') {
    return firstCatalogOrderedJumpTarget(node, n, answer.optionIds) ?? n.default;
  }
  if (n.kind === 'jump-by-boolean' && answer.kind === 'bool') {
    return answer.value ? n.whenTrue : n.whenFalse;
  }
  return 'END';
}

/**
 * True when `guard` is satisfied by at least one referenced source node's
 * answer. Trusts `answers` verbatim — it does NOT re-derive which nodes are
 * actually on the current path, so the caller owns that scoping:
 * - `walk-path.ts` builds `answers` incrementally from the steps it has
 *   already taken, so an off-path (forged or stale) entry in the raw input
 *   it was given can never satisfy a guard here.
 * - The frontend runner (`use-survey-runner.ts`) calls `resolveNext`
 *   one node at a time with its own accumulated `byNode`, which
 *   `pruneOrphanedBranchAnswers` already strips of every answer left behind
 *   by an abandoned branch as soon as the user re-answers the branching
 *   node — so by construction it never carries an off-path entry into this
 *   check either. Both callers converge on the same "path-scoped answers"
 *   invariant through different, independently-necessary mechanisms; this
 *   function itself stays a single trusting primitive rather than a second
 *   place that re-derives "on path".
 */
function isEnterWhenSatisfied(guard: EnterWhen, answers: Answers): boolean {
  if (!answers) {
    return false;
  }
  return guard.anyOf.some(({ nodeId, optionIds }) => {
    const sourceAnswer = answers.byNode[nodeId];
    if (!sourceAnswer) {
      return false;
    }
    if (sourceAnswer.kind === 'single') {
      return optionIds.includes(sourceAnswer.optionId);
    }
    if (sourceAnswer.kind === 'multi') {
      return sourceAnswer.optionIds.some((id) => optionIds.includes(id));
    }
    return false;
  });
}

/**
 * Advances a target past every consecutive node whose `enterWhen` guard is
 * unmet, following each skipped node's own `next` (guaranteed `linear` by
 * `validateSchema`'s `ENTER_WHEN_INVALID`). A node without `enterWhen`, or
 * whose guard IS met, is returned unchanged — this is a no-op for every
 * schema that does not use the feature.
 */
function skipUnmetEnterWhenNodes(
  target: string,
  answers: Answers,
  byId: Map<NodeId, SurveyNode>,
): string {
  let current = target;
  const visited = new Set<string>();
  while (current !== 'END' && !visited.has(current)) {
    visited.add(current);
    const node = byId.get(current);
    if (!node?.enterWhen || isEnterWhenSatisfied(node.enterWhen, answers)) {
      return current;
    }
    // ENTER_WHEN_INVALID guarantees `next.kind === 'linear'` for any node
    // carrying a guard, so this is always well-defined on a valid schema.
    const nextLinear =
      node.type !== 'end' && node.next?.kind === 'linear' ? node.next.nextNodeId : 'END';
    current = nextLinear;
  }
  return current;
}

/**
 * `END` on ANY inner node of a repeat-block ends the current ITERATION
 * (next iteration, or exit the block per the existing rules below) — never
 * the whole survey. A repeat-block does not guarantee its inner nodes chain
 * to one another (`inner1 -> inner2 -> ... -> END` on the last one only is
 * an authoring convention, not something the engine can assume): the
 * builder's default for a freshly-added inner node is `next: 'END'`
 * independent of position, and a real generated template
 * (`doc-taratura-termometri`'s `t_result`, not the block's last inner node)
 * hit exactly this — before this fix, `END` from a non-last inner node fell
 * through to "end of survey", silently dropping every remaining iteration
 * plus every node after the block.
 */
function resolveInsideBlock(
  target: string,
  iterCtx: IterationContext,
  schema: SurveySchema,
  answers: Answers,
): NextResolution | null {
  const rb = schema.nodes.find((x) => x.id === iterCtx.blockId);
  if (!rb || rb.type !== 'repeat-block') {
    return { kind: 'end-of-survey' };
  }
  if (target !== 'END') {
    return null;
  }
  const total = iterationCount(rb, answers);
  const nextIndex = iterCtx.index + 1;
  if (nextIndex < total) {
    if (rb.innerNodes.length === 0) {
      return { kind: 'end-of-survey' };
    }
    return {
      kind: 'goto',
      nodeId: rb.innerNodes[0].id,
      iterationContext: { blockId: rb.id, index: nextIndex },
    };
  }
  const after = rb.next?.kind === 'linear' ? rb.next.nextNodeId : 'END';
  if (after === 'END') {
    return { kind: 'end-of-survey' };
  }
  return { kind: 'goto', nodeId: after, iterationContext: null };
}

function resolveEnterBlock(
  target: string,
  schema: SurveySchema,
  answers: Answers,
): NextResolution | null {
  if (target === 'END') {
    return null;
  }
  const tgt = schema.nodes.find((x) => x.id === target);
  if (!tgt || tgt.type !== 'repeat-block') {
    return null;
  }
  const total = iterationCount(tgt, answers);
  const after = tgt.next?.kind === 'linear' ? tgt.next.nextNodeId : 'END';
  if (total === 0 || tgt.innerNodes.length === 0) {
    return after === 'END'
      ? { kind: 'end-of-survey' }
      : { kind: 'goto', nodeId: after, iterationContext: null };
  }
  return {
    kind: 'goto',
    nodeId: tgt.innerNodes[0].id,
    iterationContext: { blockId: tgt.id, index: 0 },
  };
}

/**
 * The repeat-block/enterWhen plumbing shared by every caller that already
 * knows WHICH raw target a node resolves to — `resolveNext` (target computed
 * from the answer via `staticTarget`) and `walk-path.ts`'s unanswered-node
 * advance (target computed from the node's own `linear`/`jump-by-option`
 * default, with no answer at all). Extracted so both stay a single
 * derivation instead of two copies of the block-iteration logic.
 */
export function resolveNextFromTarget(
  rawTarget: string,
  schema: SurveySchema,
  iterCtx: IterationContext | null,
  answers: Answers = null,
): NextResolution {
  const byId = new Map(schema.nodes.map((n) => [n.id, n] as const));
  const target = skipUnmetEnterWhenNodes(rawTarget, answers, byId);

  if (iterCtx) {
    const result = resolveInsideBlock(target, iterCtx, schema, answers);
    if (result) {
      return result;
    }
  }

  const enterResult = resolveEnterBlock(target, schema, answers);
  if (enterResult) {
    return enterResult;
  }

  if (target === 'END') {
    return { kind: 'end-of-survey' };
  }
  return { kind: 'goto', nodeId: target, iterationContext: iterCtx ?? null };
}

export function resolveNext(
  node: SurveyNode,
  answer: AnswerValue,
  schema: SurveySchema,
  iterCtx: IterationContext | null,
  answers: Answers = null,
): NextResolution {
  const rawTarget = staticTarget(node, answer);
  return resolveNextFromTarget(rawTarget, schema, iterCtx, answers);
}
